// The lobby (docs/platform/netcode.md §3): the server's LobbyState rendered as forms; every edit is
// a LobbyCmd. Host-only controls are disabled for everyone else (the server enforces it anyway).
// Its look is the original's online lobby (docs/platform/ClientShell.md "Lobby style"): the theme's
// animated front-end backdrop, the settings and players boxes with their header strips, the slots
// as player rows with avatars, AI difficulty, ready lights and the host's crown, the lobby chat.
import type { Catalogue, LobbyCmd, LobbyState, MatchSettings, Occupant, PlayerId, Slot } from '../net/protocol.ts';
import { assetJson } from '../assets.ts';
import { FLAT_COLOURS } from '../game/hud.ts';
import { Backdrop } from '../render/backdrop.ts';
import { artVar, fortPreviewVar } from './feart.ts';
import { guiHash } from './ip2/ip2.ts';
import { PreviewWidget } from './preview.ts';

/** The colour indices' names (TeamColours::g_FlatColour order). */
const COLOUR_NAMES = ['Red', 'Blue', 'Green', 'Purple', 'Yellow', 'Cyan'];
/** `MapSizeNames` (FE.RLG.Size.Setting1..3, the Landscape Generator's sizes). */
const SIZES = ['Small', 'Medium', 'Large'];
/** The extras a team's fort brings (`FortExtraType` 0..5, the original lobby's
 * `FortsMan::GetFortExtrasList`; names after `FortsMan::GetExtraTypeName`). */
const FORT_EXTRAS = ['None', 'Vehicle', 'Sentry gun', 'Magnet', 'Health crate', 'Craft crate'];

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

export class Lobby {
  root: HTMLElement;
  state: LobbyState | null = null;
  catalogue: Catalogue | null = null;
  me: PlayerId | null = null;
  send: (cmd: LobbyCmd) => void;
  private chatLines: string[] = [];
  onChat: ((text: string) => void) | null = null;
  /** The landscape preview (preview.ts): one element kept across renders. */
  readonly preview = new PreviewWidget();
  /** The theme's front-end backdrop behind the lobby (render/backdrop.ts); the page starts and
   * stops it around matches. */
  readonly backdrop = new Backdrop();
  /** The English text library (`Gui::TextLibFind`): the themes' and landscape types' titles. */
  private texts: Record<string, string> | null = null;

  constructor(parent: HTMLElement, send: (cmd: LobbyCmd) => void) {
    this.send = send;
    this.root = document.createElement('div');
    this.root.className = 'lobby';
    parent.append(this.backdrop.el, this.root);
    this.preview.onRandomise = () => { if (this.isHost()) this.send({ cmd: 'RandomiseLandscape' }); };
    void assetJson<{ strings: Record<string, string> }>('ui/text/Eng.json').then(t => { this.texts = t.strings; this.render(); }, () => undefined);
  }

  isHost(): boolean {
    return this.state !== null && this.state.host === this.me;
  }

  addChat(line: string): void {
    this.chatLines.push(line);
    if (this.chatLines.length > 50) this.chatLines.shift();
    this.render();
  }

  private memberName(id: PlayerId): string {
    return this.state?.members.find(m => m.id === id)?.name ?? `#${id}`;
  }

  /** The slot's player line: the member (and whether this page plays it) with the host's crown
   * and the member's connection light, an AI's difficulty, or an open slot. */
  private occupantText(o: Occupant): string {
    if (o.t === 'Open') return '<span class="who">Open slot</span>';
    if (o.t === 'Ai') return `<span class="who">AI: ${escapeHtml(this.catalogue?.ai_levels[o.level] ?? `level ${o.level}`)}</span>`;
    // The host's further slots are hot-seat teams: number them so it is clear who is who.
    const own = this.state?.slots.filter(x => x.occupant.t === 'Human' && x.occupant.player === o.player) ?? [];
    const k = own.findIndex(x => x.occupant === o);
    const local = own.length > 1 ? `, local player ${k + 1}` : '';
    const tag = o.player === this.me ? ` (you${local})` : local ? ` (${local.slice(2)})` : '';
    const on = this.state?.members.find(m => m.id === o.player)?.connected === true;
    return `<span class="who">${escapeHtml(this.memberName(o.player))}</span>${o.player === this.state?.host ? '<span class="crown" title="Host"><span class="sr-only">(host)</span></span>' : ''}`
      + `<span class="tag">${tag}</span><span class="light${on ? ' on' : ''}" title="${on ? 'Connected' : 'Not connected'}"><span class="sr-only">${on ? 'connected' : 'not connected'}</span></span>`;
  }

  render(): void {
    const s = this.state;
    if (!s) { this.root.innerHTML = '<div class="lobby-wait panel" role="status"><span class="spinner"></span>Connecting…</div>'; return; }
    const host = this.isHost();
    const cat = this.catalogue;
    const link = `${location.origin}/m/${s.room}`;
    const set = s.settings;
    const themes = cat?.themes ?? [];
    const theme = themes.find(t => t.name === set.landscape.theme);
    const dis = host ? '' : 'disabled';
    const me = s.members.find(m => m.id === this.me);
    // A fort-mode landscape: each team picks its fort and extras (the original's lobby team options).
    const fortMode = theme?.fort === true;
    this.backdrop.setTheme(set.landscape.theme);
    // Every lobby change from anyone re-renders the page: keep the input this member is typing in
    // (its text, cursor and focus), so another member's change never eats a half-typed name or chat,
    // and the chat's scroll (at its end, it follows new lines).
    const kept = this.focusedInput();
    const oldLines = this.root.querySelector('.chat-lines');
    const chatTop = oldLines && oldLines.scrollTop + oldLines.clientHeight < oldLines.scrollHeight - 8 ? oldLines.scrollTop : null;
    this.root.innerHTML = `
      <header class="lobby-head">
        <div class="lobby-logo" role="img" aria-label="Worms W.M.D"></div>
        <h1>Match lobby</h1>
        <div class="field-row lobby-link"><label for="lobby-link">Share this link</label><input readonly value="${escapeHtml(link)}" id="lobby-link"><button id="copy-link" class="btn">Copy</button></div>
        <div class="field-row lobby-name"><label for="my-name">Your name</label><input id="my-name" maxlength="24" value="${escapeHtml(me?.name ?? '')}"><button id="set-name" class="btn">Set</button>${host ? '<span class="host-badge"><span class="crown"></span>Host</span>' : ''}</div>
      </header>
      <div class="lobby-grid">
        <section class="panel settings-panel" aria-labelledby="settings-title">
          <h2 class="panel-head" id="settings-title">Match settings${host ? '' : '<span class="host-only">Host only</span>'}</h2>
          <div id="preview-slot"></div>
          <fieldset class="settings" ${dis}><legend class="sr-only">Match settings${host ? '' : ' (host only)'}</legend>
            <label class="field"><span>Scheme</span><select id="scheme">${(cat?.schemes ?? [set.scheme]).map(x => `<option ${x === set.scheme ? 'selected' : ''}>${escapeHtml(x)}</option>`).join('')}</select></label>
            <label class="field"><span>Worms per team</span><input id="worms" type="number" min="1" max="8" value="${set.worms_per_team}"></label>
            ${this.landscapeFields(s)}
            <div class="field"><label for="seed">Seed</label><span class="field-row"><input id="seed" type="number" min="0" max="4294967294" value="${set.landscape.seed}"><button id="reroll" class="btn">New landscape</button></span></div>
          </fieldset>
        </section>
        <section class="panel teams-panel" aria-labelledby="teams-title">
          <h2 class="panel-head" id="teams-title">Teams</h2>
          <ol class="slots">${s.slots.map((sl, i) => this.slotCard(sl, i, host, fortMode)).join('')}</ol>
          <div class="lobby-actions"><button id="start" class="btn btn-big" ${host ? '' : 'disabled'}>Start match</button><p class="hint">${!host ? 'Waiting for the host to start.' : fortMode ? 'Needs teams of both fort colours, Red and Blue only; every human slot connected.' : 'Needs two teams of two colours; every human slot connected.'}</p></div>
        </section>
        <section class="panel chat-panel" aria-labelledby="chat-title">
          <h2 class="panel-head" id="chat-title">Lobby chat</h2>
          <ul class="members" aria-label="In this room">${s.members.map(m => `<li class="${m.connected ? '' : 'offline'}"><span class="light${m.connected ? ' on' : ''}" title="${m.connected ? 'Connected' : 'Not connected'}"></span>${escapeHtml(m.name)}${m.id === s.host ? '<span class="crown" title="Host"><span class="sr-only">(host)</span></span>' : ''}${m.id === this.me ? '<span class="tag">(you)</span>' : ''}</li>`).join('')}</ul>
          <div class="chat"><div class="chat-lines" role="log">${this.chatLines.map(l => `<div>${escapeHtml(l)}</div>`).join('')}</div><input id="chat-in" maxlength="200" placeholder="Chat…" aria-label="Chat message"></div>
        </section>
      </div>`;
    this.wire(s, host);
    this.root.querySelector('#preview-slot')?.replaceWith(this.preview.el);
    this.preview.update(s, cat, host);
    const lines = this.root.querySelector('.chat-lines');
    if (lines) lines.scrollTop = chatTop ?? lines.scrollHeight;
    if (kept) this.restoreInput(kept);
  }

  /** The landscape controls of the original's Landscape Generator (wmd_landscapegenerator,
   * docs/port/landgen.md §1.3): the theme by its title (Fort Mode is a theme), the theme's
   * landscape types by their titles (Single Island, Caverns, Multiple Islands; the server draws
   * the type's variation), the size (a cavern template offers only Large,
   * `NDSPRIMER_List_of_Size_Settings`; the room's own size stays listed) and the building chance. */
  private landscapeFields(s: LobbyState): string {
    const land = s.settings.landscape;
    const themes = this.catalogue?.themes ?? [];
    const theme = themes.find(t => t.name === land.theme);
    const opt = (value: string | number, text: string, selected: boolean): string => `<option value="${escapeHtml(String(value))}" ${selected ? 'selected' : ''}>${escapeHtml(text)}</option>`;
    const cavern = (theme?.types[land.land_type]?.templates[land.variation] ?? '').toLowerCase().includes('cavern');
    const sizes = SIZES.map((x, i) => [i, x] as const).filter(([i]) => !cavern || i === 2 || i === land.size);
    return `
            <label class="field"><span>Theme</span><select id="theme">${(themes.length ? themes : [{ name: land.theme, title: '' }]).map(t => opt(t.name, this.texts?.[guiHash(t.title)] ?? t.name, t.name === land.theme)).join('')}</select></label>
            <label class="field"><span>Type</span><select id="land-type">${(theme?.types ?? []).map((t, i) => opt(i, this.texts?.[guiHash(t.title)] ?? t.title.replace(/^FE\.RLG\./, ''), i === land.land_type)).join('')}</select></label>
            <label class="field"><span>Size</span><select id="size">${sizes.map(([i, x]) => opt(i, x, i === land.size)).join('')}</select></label>
            <label class="field"><span>Building chance</span><select id="buildings">${opt(0, 'Off', !land.buildings)}${opt(1, 'On', land.buildings)}</select></label>`;
  }

  /** The control with focus inside the lobby (a text input, select or button): a selector that
   * finds it again after a re-render (its id or its `data-*` attribute), an input's or select's
   * value and selection. A keyboard player keeps its place when another member changes the lobby. */
  private focusedInput(): { sel: string; value: string | null; start: number | null; end: number | null } | null {
    const e = document.activeElement;
    if (!(e instanceof HTMLInputElement || e instanceof HTMLSelectElement || e instanceof HTMLButtonElement) || !this.root.contains(e)) return null;
    const data = Array.from(e.attributes).find(a => a.name.startsWith('data-'));
    const sel = e.id ? `#${e.id}` : data ? `[${data.name}="${data.value}"]` : null;
    if (!sel) return null;
    const text = e instanceof HTMLInputElement && e.type !== 'number';
    return { sel, value: e instanceof HTMLButtonElement ? null : e.value, start: text ? e.selectionStart : null, end: text ? e.selectionEnd : null };
  }

  private restoreInput(k: { sel: string; value: string | null; start: number | null; end: number | null }): void {
    const e = this.root.querySelector(k.sel);
    if (!(e instanceof HTMLInputElement || e instanceof HTMLSelectElement || e instanceof HTMLButtonElement) || e.disabled) return;
    if (k.value !== null && !(e instanceof HTMLButtonElement)) e.value = k.value;
    e.focus();
    if (e instanceof HTMLInputElement && k.start !== null) e.setSelectionRange(k.start, k.end);
  }

  private slotCard(sl: Slot, i: number, host: boolean, fortMode: boolean): string {
    const o = sl.occupant;
    const mine = o.t === 'Human' && o.player === this.me;
    const editable = mine || (host && o.t === 'Ai');
    const ai = this.catalogue?.ai_levels ?? [];
    const banks = this.catalogue?.speech_banks ?? [];
    // The forts' UI text ids (`Gui.Forts.DonkeyDig`) as display names ("Donkey Dig").
    const fortIds = this.catalogue?.forts ?? [];
    const forts = fortIds.map(f => f.replace(/^Gui\.Forts\./, '').replace(/([a-z])([A-Z])/g, '$1 $2'));
    const fort = sl.team.fort ?? 0;
    const extra = sl.team.fort_extra ?? 0;
    const colour = sl.team.colour % COLOUR_NAMES.length;
    const value = (text: string): string => `<span class="value">${text}</span>`;
    const preview = fortPreviewVar(fortIds[fort] ?? '');
    const fortFields = !fortMode || o.t === 'Open' ? '' : `
      <label class="slot-field slot-fort" title="Fort"><span class="fort-preview"${preview ? ` style="background-image: var(${preview})"` : ''}></span><span class="sr-only">Fort</span>${editable
        ? `<select data-fort="${i}">${forts.map((f, k) => `<option value="${k}" ${k === fort ? 'selected' : ''}>${escapeHtml(f)}</option>`).join('')}</select>`
        : value(escapeHtml(forts[fort] ?? ''))}</label>
      <label class="slot-field slot-extra" title="Fort extras"><span class="sr-only">Fort extras</span>${editable
        ? `<select data-extra="${i}">${FORT_EXTRAS.map((n, k) => `<option value="${k}" ${k === extra ? 'selected' : ''}>${n}</option>`).join('')}</select>`
        : value(FORT_EXTRAS[extra] ?? '')}</label>`;
    const fields = o.t === 'Open' ? `<span class="slot-team">${escapeHtml(sl.team.name)}</span>` : `
      <label class="slot-field slot-name"><span class="sr-only">Team name</span>${editable ? `<input data-team="${i}" maxlength="24" value="${escapeHtml(sl.team.name)}">` : value(escapeHtml(sl.team.name))}</label>
      <label class="slot-field slot-colour" title="Colour"><span class="swatch"></span><span class="sr-only">Colour</span>${editable
        ? `<select data-colour="${i}">${COLOUR_NAMES.map((n, c) => `<option value="${c}" ${c === sl.team.colour ? 'selected' : ''} ${fortMode && c > 1 ? 'disabled' : ''}>${n}</option>`).join('')}</select>`
        : value(COLOUR_NAMES[colour])}</label>
      <label class="slot-field slot-speech" title="Speech bank"><span class="speech-icon"></span><span class="sr-only">Speech bank</span>${editable
        ? `<select data-speech="${i}">${banks.map(b => `<option value="${escapeHtml(b)}" ${b === sl.team.speech_bank ? 'selected' : ''}>${escapeHtml(b)}</option>`).join('')}</select>`
        : value(escapeHtml(sl.team.speech_bank))}</label>${fortFields}`;
    const actions: string[] = [];
    // Everyone takes one slot. The host may add further teams of its own for players sharing its
    // screen and keyboard (hot seat); members who joined by the link may not (the server checks).
    const haveSlot = this.state?.slots.some(x => x.occupant.t === 'Human' && x.occupant.player === this.me) ?? false;
    if (o.t === 'Open' && !haveSlot) actions.push(`<button data-claim="${i}" class="btn">Take</button>`);
    if (o.t === 'Open' && haveSlot && host) actions.push(`<button data-claim="${i}" class="btn" title="Another team played on this screen and keyboard (hot seat)">Add local player</button>`);
    if (mine || (host && o.t === 'Human')) actions.push(`<button data-leave="${i}" class="btn">Leave</button>`);
    if (host) {
      actions.push(`<select data-ai="${i}" aria-label="AI player"><option value="">${o.t === 'Ai' ? 'Open' : 'Add AI…'}</option>${ai.map((n, l) => `<option value="${l}" ${o.t === 'Ai' && o.level === l ? 'selected' : ''}>AI: ${escapeHtml(n)}</option>`).join('')}</select>`);
    }
    // The avatar: a human's worm face (one per slot), an AI's difficulty insignia
    // (FE_AIDifficulty_01..05), an open slot's silhouette.
    const avatar = o.t === 'Human' ? `hu_playeravatar_0${(i % 6) + 1}` : o.t === 'Ai' ? `fe_aidifficulty_0${Math.min(o.level, 4) + 1}` : 'hu_playerpic_01';
    return `<li class="slot slot-${o.t.toLowerCase()}${mine ? ' mine' : ''}" style="--team: rgb(${FLAT_COLOURS[colour].join(',')})">
      <div class="slot-avatar"><span style="background-image: var(${artVar(avatar)})"></span></div>
      <div class="slot-main"><div class="slot-player">${this.occupantText(o)}</div><div class="slot-fields">${fields}</div></div>
      <div class="slot-actions">${actions.join('')}</div></li>`;
  }

  private wire(s: LobbyState, host: boolean): void {
    const $ = (id: string): HTMLElement | null => this.root.querySelector(`#${id}`);
    const val = (id: string): string => { const e = $(id); return e instanceof HTMLInputElement || e instanceof HTMLSelectElement ? e.value : ''; };
    $('copy-link')?.addEventListener('click', () => { void navigator.clipboard?.writeText(val('lobby-link')); });
    $('set-name')?.addEventListener('click', () => { const n = val('my-name').trim(); if (n) this.send({ cmd: 'SetName', name: n }); });
    for (const b of this.root.querySelectorAll<HTMLElement>('[data-claim]')) b.addEventListener('click', () => this.send({ cmd: 'ClaimSlot', slot: Number(b.dataset.claim) }));
    for (const b of this.root.querySelectorAll<HTMLElement>('[data-leave]')) b.addEventListener('click', () => this.send({ cmd: 'LeaveSlot', slot: Number(b.dataset.leave) }));
    for (const e of this.root.querySelectorAll<HTMLSelectElement>('select[data-ai]')) {
      e.addEventListener('change', () => this.send({ cmd: 'SetAi', slot: Number(e.dataset.ai), level: e.value === '' ? null : Number(e.value) }));
    }
    const teamEdit = (slot: number, patch: { name?: string; colour?: number; speech_bank?: string; fort?: number; fort_extra?: number }): void => {
      const t = s.slots[slot].team;
      this.send({ cmd: 'SetTeam', slot, team: {
        name: patch.name ?? t.name, colour: patch.colour ?? t.colour, worms: t.worms, speech_bank: patch.speech_bank ?? t.speech_bank,
        fort: patch.fort ?? t.fort ?? 0, fort_extra: patch.fort_extra ?? t.fort_extra ?? 0,
      } });
    };
    for (const e of this.root.querySelectorAll<HTMLInputElement>('input[data-team]')) e.addEventListener('change', () => { if (e.value.trim()) teamEdit(Number(e.dataset.team), { name: e.value.trim() }); });
    for (const e of this.root.querySelectorAll<HTMLSelectElement>('select[data-colour]')) e.addEventListener('change', () => teamEdit(Number(e.dataset.colour), { colour: Number(e.value) }));
    for (const e of this.root.querySelectorAll<HTMLSelectElement>('select[data-speech]')) e.addEventListener('change', () => teamEdit(Number(e.dataset.speech), { speech_bank: e.value }));
    for (const e of this.root.querySelectorAll<HTMLSelectElement>('select[data-fort]')) e.addEventListener('change', () => teamEdit(Number(e.dataset.fort), { fort: Number(e.value) }));
    for (const e of this.root.querySelectorAll<HTMLSelectElement>('select[data-extra]')) e.addEventListener('change', () => teamEdit(Number(e.dataset.extra), { fort_extra: Number(e.value) }));
    if (host) {
      const settings = (): MatchSettings => ({
        scheme: val('scheme') || s.settings.scheme,
        options: s.settings.options,
        worms_per_team: Math.max(1, Math.min(8, Number(val('worms')) || s.settings.worms_per_team)),
        // Theme, type and variation as the room has them (they change by their own commands).
        landscape: { ...s.settings.landscape, size: Number(val('size')), buildings: val('buildings') === '1', seed: (Number(val('seed')) >>> 0) },
      });
      for (const id of ['scheme', 'worms', 'size', 'buildings', 'seed']) $(id)?.addEventListener('change', () => this.send({ cmd: 'SetSettings', settings: settings() }));
      // The Landscape Generator's theme and type: the server draws the type's variation.
      $('theme')?.addEventListener('change', () => this.send({ cmd: 'SetTheme', theme: val('theme') }));
      $('land-type')?.addEventListener('change', () => this.send({ cmd: 'SetLandType', land_type: Number(val('land-type')) }));
      // GENERATE: a new world seed and variation of the type.
      $('reroll')?.addEventListener('click', () => this.send({ cmd: 'RerollLandscape' }));
      $('start')?.addEventListener('click', () => this.send({ cmd: 'Start' }));
    }
    const chat = $('chat-in');
    if (chat instanceof HTMLInputElement) {
      chat.addEventListener('keydown', e => {
        if (e.key === 'Enter' && chat.value.trim()) { this.onChat?.(chat.value.trim()); chat.value = ''; }
      });
    }
  }
}
